import {
  memo,
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format, isValid, parse, parseISO } from 'date-fns';
import type { Project, ProjectStatusEventBrief } from '@/api/client';
import { api, type UpdateProjectData, type User } from '@/api/client';
import { useAuth } from '@/contexts/useAuth';
import { useToast } from '@/contexts/ToastContext';
import { ContextMenu, type ContextMenuItem } from '@/components/ui/ContextMenu/ContextMenu';
import { BookmarkIcon, InfoIcon } from '@/components/ui/Icons';
import { useCellCommentShellOptional } from '@/features/project-cell-comments/cellCommentShellContext';
import { DateAutofillInput } from '@/components/ui/DateAutofillInput/DateAutofillInput';
import { TimeInput } from '@/components/ui/TimeInput/TimeInput';
import { Button } from '@/components/ui/Button/Button';
import { DateLogChangedByField } from './DateLogChangedByField';
import { userDisplayNameOrPlaceholder } from '@/utils/userDisplayName';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { useClickOutside } from '@/hooks/useClickOutside';
import { markProjectUpdatedByMutation } from '@/lib/projectRealtimeDedup';
import {
  captureProjectListRollback,
  mergeSingleProjectInCaches,
  optimisticPatchReadyMonthSummariesAfterBucketMove,
  patchProjectListCaches,
  syncProjectListCachesAfterStatusEventsChanged,
  type ProjectListRollbackContext,
} from '@/lib/projectListQueryCache';
import { PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY } from '@/lib/projectEntityCache';
import { useProjectRowOpenDetails } from './ProjectRowProjectDetailsContext';
import { rollbackProjectsAndReconcile } from '@/lib/projectMutationReconcile';
import {
  EVENT_TO_BASE_STATE,
  STATE_DISPLAY_ORDER,
  STATUS_EVENT_TYPE_LABELS,
  STATUS_LOG_EDIT_EVENT_ORDER,
  type StatusLogEditEventType,
  type StatusLogStateLabel,
} from '@/utils/statusEventToState';
import { getReadyByMonthBucketFromProject } from '@/utils/readyMonthStats';
import { parseStatusEventTime, statusEventOccurredAtIso } from '@/utils/statusEventOccurredAt';
import { isProjectExcludedFromActiveTable } from '@/utils/projectStatuses';
import './DateLogCell.css';

function formatCellDate(iso: string) {
  const d = parseISO(iso);
  if (!isValid(d)) return '';
  return format(d, 'dd.MM.yy');
}

function isoToDateAndTime(iso: string): { date: string; time: string } {
  const d = parseISO(iso);
  if (!isValid(d)) return { date: '', time: '' };
  return {
    date: format(d, 'dd.MM.yyyy'),
    time: format(d, 'HH:mm:ss'),
  };
}

function parseDateDraft(s: string): Date | null {
  const t = s.trim();
  if (!t) return null;
  for (const fmt of ['dd.MM.yyyy', 'dd.MM.yy', 'd.M.yyyy', 'd.M.yy'] as const) {
    const d = parse(t, fmt, new Date());
    if (isValid(d)) return d;
  }
  return null;
}

function occurredAtMs(iso: string): number {
  return new Date(iso).getTime();
}

/** Сравнение моментов времени; строковое > ненадёжно (Z vs +00:00, доли секунды). */
function isLaterOccurredAt(a: string, b: string): boolean {
  const ta = occurredAtMs(a);
  const tb = occurredAtMs(b);
  if (Number.isFinite(ta) && Number.isFinite(tb)) return ta > tb;
  return a > b;
}

function sameOccurredInstant(a: string, b: string): boolean {
  const ta = occurredAtMs(a);
  const tb = occurredAtMs(b);
  if (Number.isFinite(ta) && Number.isFinite(tb)) return ta === tb;
  return a === b;
}

function samePerson(a: string | null | undefined, b: string): boolean {
  return (a ?? '') === b;
}

function isStatusLogLabel(s: string): s is StatusLogStateLabel {
  return (STATE_DISPLAY_ORDER as readonly string[]).includes(s);
}

type RowDraft = { date: string; time: string; changedByPersonId: string; changedByLabel?: string };

function emptyRowDrafts(): Record<StatusLogEditEventType, RowDraft> {
  return Object.fromEntries(
    STATUS_LOG_EDIT_EVENT_ORDER.map((et) => [et, { date: '', time: '', changedByPersonId: '' }])
  ) as Record<StatusLogEditEventType, RowDraft>;
}

type Winner = {
  id: string;
  event_type: string;
  occurred_at: string;
  changed_by_person_id?: string | null;
  changed_by_name?: string | null;
};

/** Снимок на момент «Сохранить» — без устаревших замыканий в mutationFn. */
type DateLogSavePayload = {
  drafts: Record<StatusLogEditEventType, RowDraft>;
  winnersByEventType: Partial<Record<string, Winner>>;
  project: Project;
  nameByIdFromProject: Map<string, string>;
  userId: string;
};

/** Blur даты/времени может ещё не произойти — приводим к тому, что съест draftRowToOccurredAtIso. */
function normalizeRowDraftForSave(row: RowDraft): RowDraft {
  let date = row.date;
  if (date.trim()) {
    const p = parseDateDraft(date);
    if (p) date = format(p, 'dd.MM.yyyy');
  }
  let time = row.time;
  if (time.trim()) {
    const t = time.trim();
    const m = t.match(/^(\d{1,2}):(\d{2})$/);
    if (m) {
      const h = Number(m[1]);
      const mm = m[2]!;
      if (h <= 23 && Number(mm) <= 59) {
        time = `${String(h).padStart(2, '0')}:${mm}:00`;
      }
    }
  }
  return { ...row, date, time };
}

function normalizeDraftsForSave(
  drafts: Record<StatusLogEditEventType, RowDraft>
): Record<StatusLogEditEventType, RowDraft> {
  return Object.fromEntries(
    STATUS_LOG_EDIT_EVENT_ORDER.map((et) => [et, normalizeRowDraftForSave(drafts[et])])
  ) as Record<StatusLogEditEventType, RowDraft>;
}

/** Как в saveMutation: пустая дата → null; иначе ISO или throw. */
function draftRowToOccurredAtIso(
  eventType: StatusLogEditEventType,
  row: RowDraft
): string | null {
  if (!row.date.trim()) return null;
  const datePart = parseDateDraft(row.date);
  if (!datePart) {
    throw new Error(`Некорректная дата для «${STATUS_EVENT_TYPE_LABELS[eventType]}»`);
  }
  const tp = parseStatusEventTime(row.time);
  if (!tp) {
    throw new Error(`Некорректное время для «${STATUS_EVENT_TYPE_LABELS[eventType]}»`);
  }
  return statusEventOccurredAtIso(datePart, tp);
}

/** Локальный прогноз status_events после сохранения черновиков — для оптимистичного переноса по месяцам. */
function buildOptimisticProjectFromDateDrafts(
  base: Project,
  drafts: Record<StatusLogEditEventType, RowDraft>,
  winnersByEventType: Partial<Record<string, Winner>>,
  fallbackPersonId: string,
  nameByIdFromProject: Map<string, string>
): Project {
  const events: ProjectStatusEventBrief[] = (base.status_events ?? []).map((e) => ({ ...e }));

  for (const eventType of STATUS_LOG_EDIT_EVENT_ORDER) {
    const row = drafts[eventType];
    const occurred_at = draftRowToOccurredAtIso(eventType, row);
    if (occurred_at === null) continue;

    const personId = row.changedByPersonId.trim() || fallbackPersonId;
    const displayName =
      row.changedByLabel?.trim() || nameByIdFromProject.get(personId) || 'Пользователь';
    const winner = winnersByEventType[eventType];
    if (winner) {
      if (
        sameOccurredInstant(winner.occurred_at, occurred_at) &&
        samePerson(winner.changed_by_person_id, personId)
      ) {
        continue;
      }
      const idx = events.findIndex((e) => e.id === winner.id);
      if (idx >= 0) {
        events[idx] = {
          ...events[idx]!,
          occurred_at,
          changed_by_person_id: personId,
          changed_by_name: displayName,
        };
      }
    } else {
      events.push({
        id: `optimistic-${eventType}-${base.id}`,
        event_type: eventType,
        occurred_at,
        changed_by_person_id: personId,
        changed_by_name: displayName,
      });
    }
  }

  return { ...base, status_events: events };
}

interface DateLogCellProps {
  project: Project;
}

export const DateLogCell = memo(function DateLogCell({ project }: DateLogCellProps) {
  const cellCommentShell = useCellCommentShellOptional();
  const openProjectDetailsFromRow = useProjectRowOpenDetails();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [rowDrafts, setRowDrafts] = useState<Record<StatusLogEditEventType, RowDraft>>(emptyRowDrafts);
  const popoverRef = useRef<HTMLDivElement>(null);
  const cellRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  triggerRef.current = popoverOpen && cellRef.current ? cellRef.current : null;

  const { displayByState, winnersByEventType } = useMemo(() => {
    const events = project.status_events ?? [];
    const display: Partial<Record<StatusLogStateLabel, string>> = {};
    const byEt: Partial<Record<string, Winner>> = {};

    for (const e of events) {
      const stateRaw = EVENT_TO_BASE_STATE[e.event_type];
      if (stateRaw && isStatusLogLabel(stateRaw)) {
        const existingAt = display[stateRaw];
        if (!existingAt || isLaterOccurredAt(e.occurred_at, existingAt)) {
          display[stateRaw] = e.occurred_at;
        }
      }

      if (!e.id) continue;
      const w: Winner = {
        id: e.id,
        event_type: e.event_type,
        occurred_at: e.occurred_at,
        changed_by_person_id: e.changed_by_person_id,
        changed_by_name: e.changed_by_name,
      };
      const cur = byEt[e.event_type];
      if (!cur || isLaterOccurredAt(e.occurred_at, cur.occurred_at)) {
        byEt[e.event_type] = w;
      }
    }
    return { displayByState: display, winnersByEventType: byEt };
  }, [project.status_events]);

  const { shouldRender, contentClasses, requestClose } = useOverlayAnimation(
    popoverOpen,
    useCallback(() => {
      setPopoverOpen(false);
    }, []),
    true
  );

  usePopoverPositioning({
    anchorEl: triggerRef.current ?? undefined,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionY: 'bottom',
    preferredPositionX: 'left',
    offset: 8,
    zIndex: 1000,
    dependencies: [shouldRender, popoverOpen],
  });

  useClickOutside(popoverRef, () => requestClose(), {
    enabled: shouldRender,
    excludeRefs: [],
    excludeSelector: '.context-menu, [data-combo-dropdown]',
    capture: false,
  });

  useEffect(() => {
    if (!shouldRender) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shouldRender, requestClose]);

  const nameByIdFromProject = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of project.status_events ?? []) {
      const pid = e.changed_by_person_id;
      if (!pid) continue;
      const raw = e.changed_by_name?.trim();
      m.set(pid, raw || 'Пользователь');
    }
    return m;
  }, [project.status_events]);

  const saveMutation = useMutation<Project, Error, DateLogSavePayload, ProjectListRollbackContext | undefined>({
    mutationFn: async (vars: DateLogSavePayload): Promise<Project> => {
      const { drafts, winnersByEventType, project: proj, userId } = vars;

      for (const eventType of STATUS_LOG_EDIT_EVENT_ORDER) {
        const row = drafts[eventType];
        const occurred_at = draftRowToOccurredAtIso(eventType, row);
        if (occurred_at === null) continue;

        const personId = row.changedByPersonId.trim() || userId;
        const winner = winnersByEventType[eventType];

        if (winner) {
          if (
            sameOccurredInstant(winner.occurred_at, occurred_at) &&
            samePerson(winner.changed_by_person_id, personId)
          ) {
            continue;
          }
          await api.projectStatusEvents.update(proj.id, winner.id, {
            occurred_at,
            changed_by_user_id: personId,
          });
        } else {
          const created = await api.projectStatusEvents.create(proj.id, {
            event_type: eventType,
            changed_by_user_id: personId,
          });
          await api.projectStatusEvents.update(proj.id, created.id, {
            occurred_at,
            changed_by_user_id: personId,
          });
        }
      }

      return api.projects.get(proj.id);
    },
    onMutate: async (vars: DateLogSavePayload) => {
      const { drafts, winnersByEventType, project: proj, nameByIdFromProject: nameMap, userId } = vars;
      const optimistic = buildOptimisticProjectFromDateDrafts(
        proj,
        drafts,
        winnersByEventType,
        userId,
        nameMap
      );
      const prevB = getReadyByMonthBucketFromProject(proj);
      const nextB = getReadyByMonthBucketFromProject(optimistic);
      const rollback = captureProjectListRollback(queryClient);
      await queryClient.cancelQueries({ queryKey: PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY });
      markProjectUpdatedByMutation(proj.id);
      syncProjectListCachesAfterStatusEventsChanged(queryClient, proj, optimistic, {
        skipReadyMonthSummariesInvalidation: true,
      });
      if (isProjectExcludedFromActiveTable(optimistic.state)) {
        optimisticPatchReadyMonthSummariesAfterBucketMove(queryClient, { prevB, nextB });
      }
      return rollback;
    },
    onSuccess: (fresh, vars) => {
      syncProjectListCachesAfterStatusEventsChanged(queryClient, vars.project, fresh);
      showSuccess('Даты сохранены');
      requestClose();
    },
    onError: (e: Error, _vars, ctx: ProjectListRollbackContext | undefined) => {
      void rollbackProjectsAndReconcile(queryClient, ctx).catch(() => {});
      showError(e.message);
    },
  });

  const highlightMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectData }) => api.projects.update(id, data),
    onMutate: async ({ id, data }) => {
      const rollback = captureProjectListRollback(queryClient);
      await queryClient.cancelQueries({ queryKey: PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY });
      const nextFlag = !!data.is_project_highlighted;
      patchProjectListCaches(queryClient, (prev) =>
        prev?.map((p) => (p.id === id ? { ...p, is_project_highlighted: nextFlag } : p))
      );
      markProjectUpdatedByMutation(id);
      return rollback;
    },
    onError: (err, _vars, ctx) => {
      void rollbackProjectsAndReconcile(queryClient, ctx).catch(() => {});
      showError(err instanceof Error ? err.message : String(err));
    },
    onSuccess: (fresh) => {
      showSuccess('Выделение обновлено');
      mergeSingleProjectInCaches(queryClient, fresh);
    },
  });

  const openEditPopover = useCallback(() => {
    const next = emptyRowDrafts();
    for (const eventType of STATUS_LOG_EDIT_EVENT_ORDER) {
      const w = winnersByEventType[eventType];
      if (w) {
        const dt = isoToDateAndTime(w.occurred_at);
        next[eventType] = {
          date: dt.date,
          time: dt.time,
          changedByPersonId: w.changed_by_person_id ?? '',
          changedByLabel: w.changed_by_name?.trim() || 'Пользователь',
        };
      } else {
        next[eventType] = {
          date: '',
          time: '',
          changedByPersonId: '',
          changedByLabel: undefined,
        };
      }
    }
    setRowDrafts(next);
    setPopoverOpen(true);
  }, [winnersByEventType]);

  const handleEditFromMenu = useCallback(() => {
    setContextMenuOpen(false);
    openEditPopover();
  }, [openEditPopover]);

  const handleCellContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setContextMenuOpen(true);
  }, []);

  const highlighted = !!project.is_project_highlighted;

  const contextMenuItems = useMemo(() => {
    const items: ContextMenuItem[] = [{ label: 'Редактировать', onClick: handleEditFromMenu }];
    if (openProjectDetailsFromRow) {
      items.push({
        label: 'Информация о проекте',
        icon: <InfoIcon size={18} />,
        onClick: () => {
          setContextMenuOpen(false);
          openProjectDetailsFromRow(cellRef.current);
        },
      });
    }
    if (cellCommentShell) items.push(cellCommentShell.addCommentMenuItem);
    items.push({
      label: highlighted ? 'Снять выделение' : 'Выделить проект',
      icon: highlighted ? (
        <i className="fa-regular fa-bookmark editable-cell__context-menu-fa" aria-hidden />
      ) : (
        <BookmarkIcon size={18} />
      ),
      onClick: () => {
        setContextMenuOpen(false);
        highlightMutation.mutate({
          id: project.id,
          data: { is_project_highlighted: !highlighted },
        });
      },
    });
    return items;
  }, [cellCommentShell, handleEditFromMenu, highlightMutation, highlighted, openProjectDetailsFromRow, project.id]);

  const handleSave = useCallback(() => {
    if (!user?.id) {
      showError('Войдите в систему, чтобы менять даты');
      return;
    }
    const drafts = normalizeDraftsForSave(rowDrafts);
    saveMutation.mutate({
      drafts,
      winnersByEventType,
      project,
      nameByIdFromProject,
      userId: user.id,
    });
  }, [saveMutation, rowDrafts, winnersByEventType, project, nameByIdFromProject, user?.id, showError]);

  const setRowDate = useCallback((eventType: StatusLogEditEventType, date: string) => {
    setRowDrafts((prev) => ({ ...prev, [eventType]: { ...prev[eventType], date } }));
  }, []);

  const setRowTime = useCallback((eventType: StatusLogEditEventType, time: string) => {
    setRowDrafts((prev) => ({ ...prev, [eventType]: { ...prev[eventType], time } }));
  }, []);

  const setRowChangedBy = useCallback((eventType: StatusLogEditEventType, u: User | null) => {
    setRowDrafts((prev) => ({
      ...prev,
      [eventType]: {
        ...prev[eventType],
        changedByPersonId: u?.id ?? '',
        changedByLabel: u ? userDisplayNameOrPlaceholder(u) : undefined,
      },
    }));
  }, []);

  const userValueForRow = useCallback(
    (row: RowDraft): User | null => {
      const id = row.changedByPersonId.trim();
      if (!id) return null;
      const name =
        row.changedByLabel?.trim() || nameByIdFromProject.get(id) || 'Пользователь';
      return { id, first_name: '', last_name: '', name };
    },
    [nameByIdFromProject]
  );

  const tableRows = useMemo(() => {
    return STATE_DISPLAY_ORDER.map((state) => {
      const at = displayByState[state];
      const shown = at ? formatCellDate(at) : '';
      return (
        <tr key={state}>
          <td>{state}</td>
          <td>{shown || ''}</td>
        </tr>
      );
    });
  }, [displayByState]);

  const popoverAnchorStyle: React.CSSProperties = {
    position: 'fixed',
    left: -9999,
    top: -9999,
    pointerEvents: 'auto',
  };
  const popoverPanelStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'var(--overlay-bg)',
    backdropFilter: 'var(--overlay-blur)',
    WebkitBackdropFilter: 'var(--overlay-blur)',
    border: 'var(--overlay-border)',
    borderRadius: 'var(--overlay-radius)',
    boxShadow: 'var(--overlay-shadow)',
  };

  return (
    <div
      ref={cellRef}
      className="projects-table__dates-cell date-log-cell--editable"
      onContextMenu={handleCellContextMenu}
    >
      <table className="projects-table__dates-table">
        <tbody>{tableRows}</tbody>
      </table>
      <ContextMenu
        isOpen={contextMenuOpen}
        onClose={() => setContextMenuOpen(false)}
        anchorPosition={contextMenuPos}
        items={contextMenuItems}
      />
      {shouldRender &&
        createPortal(
          <div ref={popoverRef} style={popoverAnchorStyle}>
            <OverlayIntrinsicSize>
              <div
                style={popoverPanelStyle}
                className={`editable-cell-popover date-log-edit-popover ${contentClasses}`}
              >
            <OverlayScrollArea
              className="editable-cell-popover__form editable-cell-popover__form_has-overlay-scroll"
              osOptions={{ scrollbars: { visibility: 'hidden' } }}
            >
              <div className="date-log-edit__scroll-inner">
                <div className="editable-cell-popover__header">
                  Даты статусов
                </div>
                <div className="editable-cell-popover__form-stack date-log-edit__rows">
                  {STATUS_LOG_EDIT_EVENT_ORDER.map((eventType) => {
                    const row = rowDrafts[eventType];
                    const isRowEmpty = !row.date.trim();
                    return (
                      <div
                        key={eventType}
                        className={`date-log-edit__row${isRowEmpty ? ' date-log-edit__row--empty' : ''}`}
                      >
                        <span className="date-log-edit__label" title={STATUS_EVENT_TYPE_LABELS[eventType]}>
                          {STATUS_EVENT_TYPE_LABELS[eventType]}
                        </span>
                        <DateAutofillInput
                          value={row.date}
                          onChange={(v) => setRowDate(eventType, v)}
                          placeholder="Дата"
                          fullWidth
                        />
                        <div className="date-log-edit__time">
                          <TimeInput
                            value={row.time}
                            onChange={(v) => setRowTime(eventType, v)}
                            placeholder="Время"
                            fullWidth
                          />
                        </div>
                        <div className="date-log-edit__by">
                          <DateLogChangedByField
                            value={userValueForRow(row)}
                            onChange={(u) => setRowChangedBy(eventType, u)}
                            placeholder="Пользователь"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </OverlayScrollArea>
            <div className="editable-cell-popover__footer">
              <Button
                type="button"
                variant="primary"
                onClick={handleSave}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </div>
              </div>
            </OverlayIntrinsicSize>
          </div>,
          document.body
        )}
    </div>
  );
});
